/** Единственный QueryClient приложения.
 *
 *  Вынесен из main.tsx, чтобы до него мог дотянуться AuthProvider:
 *  кеш принадлежит сессии, и при смене пользователя его нужно очищать.
 *  AuthProvider обёрнут снаружи QueryClientProvider, поэтому `useQueryClient`
 *  ему недоступен, а импорт main.tsx создал бы цикл.
 */
import { MutationCache, QueryClient } from "@tanstack/react-query";

import { pushGlobalToast } from "../components/ToastProvider";

// Глобальная реакция на ошибки мутаций: если мутация упала и у неё нет
// собственного onError — показываем toast, а не молчим.
export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5_000, refetchOnWindowFocus: false } },
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.options.onError) return; // локальная обработка есть
      const raw = error instanceof Error ? error.message : String(error);
      // ответ backend — JSON {"detail": "..."}: достаём человекочитаемое
      let text = raw;
      try {
        const parsed = JSON.parse(raw) as { detail?: unknown };
        if (typeof parsed.detail === "string") text = parsed.detail;
      } catch {
        // не JSON — оставляем как есть
      }
      pushGlobalToast("error", `Ошибка: ${text.slice(0, 300)}`);
    },
  }),
});

/** Забыть всё, что было загружено прежней сессией.
 *
 *  Кеш запросов переживает выход и вход в той же вкладке: страница не
 *  перезагружается, QueryClient живёт дальше. Ключи запросов пользователя
 *  почти нигде не содержат, поэтому новый пользователь видел данные
 *  прежнего — проверено опытом: сервер руководителю подразделения
 *  отдавал 0 закупок, а на дашборде стояло 3 от закупщика.
 */
export function resetQueryCache(): void {
  queryClient.cancelQueries();
  queryClient.clear();
}
